/**
 * Accordion — smooth animated open/close.
 * Web Animations API height transition on native <details>/<summary>.
 */
(function () {
  'use strict';

  const DURATION = 300;
  const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

  class AccordionComponent extends HTMLElement {
    #animation = null;
    #isClosing = false;
    #isOpening = false;

    connectedCallback() {
      const summary = this.querySelector('summary');
      if (summary) {
        summary.addEventListener('click', this.#onClick);
      }
    }

    disconnectedCallback() {
      const summary = this.querySelector('summary');
      if (summary) {
        summary.removeEventListener('click', this.#onClick);
      }
    }

    get details() {
      return this.querySelector('details');
    }

    get body() {
      return this.querySelector('.accordion__body');
    }

    #onClick = (event) => {
      event.preventDefault();

      const details = this.details;
      if (!details) return;

      // Don't allow rapid toggling while animating
      details.style.overflow = 'hidden';

      if (this.#isClosing || !details.open) {
        this.#closeSiblings();
        this.#open();
      } else if (this.#isOpening || details.open) {
        this.#close();
      }
    };

    #closeSiblings() {
      const group = this.closest('[data-accordion-group]');
      if (!group) return;
      group.querySelectorAll('accordion-component').forEach((acc) => {
        if (acc !== this && acc.details?.open) acc.close();
      });
    }

    close() {
      if (this.details?.open) this.#close();
    }

    #open() {
      const details = this.details;
      const body = this.body;
      if (!details || !body) return;

      this.#isOpening = true;

      // Get the current height (header only)
      const startHeight = details.offsetHeight;

      // Open the details to measure full height
      details.open = true;
      details.dataset.state = 'opening';

      // Calculate end height
      const endHeight = details.querySelector('summary').offsetHeight + body.scrollHeight;

      // Cancel any running animation
      this.#animation?.cancel();

      this.#animation = details.animate(
        { height: [`${startHeight}px`, `${endHeight}px`] },
        { duration: DURATION, easing: EASING }
      );

      this.#animation.onfinish = () => this.#onOpenFinish();
      this.#animation.oncancel = () => { this.#isOpening = false; };
    }

    #onOpenFinish() {
      const details = this.details;
      if (!details) return;

      details.open = true;
      details.dataset.state = 'open';
      details.style.height = '';
      details.style.overflow = '';
      this.#isOpening = false;
      this.#animation = null;
    }

    #close() {
      const details = this.details;
      const body = this.body;
      if (!details || !body) return;

      this.#isClosing = true;
      details.dataset.state = 'closing';

      const startHeight = details.offsetHeight;
      const endHeight = details.querySelector('summary').offsetHeight;

      this.#animation?.cancel();

      this.#animation = details.animate(
        { height: [`${startHeight}px`, `${endHeight}px`] },
        { duration: DURATION, easing: EASING }
      );

      this.#animation.onfinish = () => this.#onCloseFinish();
      this.#animation.oncancel = () => { this.#isClosing = false; };
    }

    #onCloseFinish() {
      const details = this.details;
      if (!details) return;

      details.open = false;
      details.dataset.state = 'closed';
      details.style.height = '';
      details.style.overflow = '';
      this.#isClosing = false;
      this.#animation = null;
    }
  }

  if (!customElements.get('accordion-component')) {
    customElements.define('accordion-component', AccordionComponent);
  }
})();
